import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Hoodie,
  ShoppingBagOpen,
  Truck,
  Sparkle,
  ArrowLeft,
  CheckCircle,
  Package,
  Storefront,
  Star,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getListing,
  getCouriers,
  createCheckoutSession,
  sellerReviews,
  formatPrice,
  fileUrl,
  imageSrc,
} from "../lib/api";
import { useAuth, formatApiError } from "../context/AuthContext";

const Stars = ({ rating }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        weight={i <= Math.round(rating) ? "fill" : "regular"}
        className={i <= Math.round(rating) ? "text-[#0A0A0A]" : "text-[#4A4A4A]"}
      />
    ))}
  </span>
);

export default function Listing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], rating: { average: 0, count: 0 } });
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [courier, setCourier] = useState("evri");
  const [couriers, setCouriers] = useState([]);
  const [address, setAddress] = useState("");
  const [buying, setBuying] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [it, cs] = await Promise.all([getListing(id), getCouriers()]);
      setItem(it);
      setCouriers(cs.couriers || []);
      if (it.seller_id) {
        const rev = await sellerReviews(it.seller_id);
        setReviews(rev);
      }
    } catch {
      toast.error("Listing not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  if (loading || !item)
    return (
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="nb-card bg-white">Loading…</div>
      </div>
    );

  const photos = (item.images && item.images.length > 0)
    ? item.images.map((fid) => fileUrl(fid))
    : (imageSrc(item) ? [imageSrc(item)] : []);

  const stock = item.stock ?? 0;
  const isOwn = user && item.seller_id === user.id;
  const courierObj = couriers.find((c) => c.id === courier);
  const shippingPrice = courierObj ? courierObj.price : 0;
  const subtotal = (item.listing_price || 0) * qty;
  const total = subtotal + shippingPrice;

  const buy = async () => {
    if (!user) {
      toast("Log in to buy");
      navigate("/login", { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    if (isOwn) return toast.error("You can't buy your own listing");
    if (stock <= 0) return toast.error("Out of stock");
    setBuying(true);
    try {
      const session = await createCheckoutSession({
        item_id: id,
        quantity: qty,
        courier,
        shipping_address: address || null,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe-hosted checkout
      window.location.href = session.url;
    } catch (e) {
      toast.error(formatApiError(e, "Could not start checkout"));
      setBuying(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
      <Link to="/" data-testid="back-marketplace" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline">
        <ArrowLeft weight="bold" size={16} /> Marketplace
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="nb-card bg-white">
            <div className="relative flex h-[460px] w-full items-center justify-center overflow-hidden rounded-xl border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
              {photos.length > 0 ? (
                <img src={photos[activeImg]} alt={item.product_name} className="h-full w-full object-contain" />
              ) : (
                <Hoodie weight="duotone" size={88} />
              )}
              {photos.length > 1 && (
                <>
                  <button
                    data-testid="prev-image"
                    onClick={() => setActiveImg((i) => (i - 1 + photos.length) % photos.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-[#0A0A0A] bg-white shadow-[3px_3px_0_0_#0A0A0A]"
                  >
                    <CaretLeft weight="bold" size={18} />
                  </button>
                  <button
                    data-testid="next-image"
                    onClick={() => setActiveImg((i) => (i + 1) % photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-[#0A0A0A] bg-white shadow-[3px_3px_0_0_#0A0A0A]"
                  >
                    <CaretRight weight="bold" size={18} />
                  </button>
                </>
              )}
            </div>
            {photos.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`h-16 w-16 overflow-hidden rounded-lg border-[3px] ${i === activeImg ? "border-[#0A0A0A] shadow-[3px_3px_0_0_#0A0A0A]" : "border-[#0A0A0A]/30"}`}
                  >
                    <img src={p} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {item.brand && <span className="nb-badge bg-[#D4C4FB]">{item.brand}</span>}
              {item.category && <span className="nb-badge bg-white">{item.category}</span>}
              {item.condition && <span className="nb-badge bg-[#B4F0A7]">{item.condition}</span>}
              {item.color && <span className="nb-badge bg-white">{item.color}</span>}
              {item.size_guess && <span className="nb-badge bg-[#FFDF85]">{item.size_guess}</span>}
            </div>
          </div>

          <div className="nb-card bg-[#FDFBF7]">
            <span className="nb-label">Description</span>
            <p className="mt-3 whitespace-pre-line text-base font-medium leading-relaxed">{item.suggested_description}</p>
            {item.keywords?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {item.keywords.map((k) => (
                  <span key={k} className="rounded-full border-[2px] border-[#0A0A0A] bg-white px-2.5 py-0.5 text-xs font-bold uppercase">
                    #{k}
                  </span>
                ))}
              </div>
            )}
          </div>

          {reviews.rating.count > 0 && (
            <div className="nb-card bg-white">
              <div className="flex items-center justify-between">
                <span className="nb-label">Seller reviews</span>
                <div className="flex items-center gap-2">
                  <Stars rating={reviews.rating.average} />
                  <span className="font-display text-sm font-bold">
                    {reviews.rating.average.toFixed(1)} · {reviews.rating.count}
                  </span>
                </div>
              </div>
              <div className="mt-3 space-y-3">
                {reviews.reviews.slice(0, 3).map((r) => (
                  <div key={r.id} className="rounded-lg border-[2px] border-[#0A0A0A] p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider">@{r.buyer_name}</span>
                      <Stars rating={r.rating} />
                    </div>
                    {r.comment && <p className="mt-1 text-sm font-medium">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="nb-card bg-white">
            <span className="nb-label">Listing</span>
            <h1 className="mt-2 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl">
              {item.suggested_title || item.product_name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm font-bold uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <Storefront weight="bold" size={14} /> @{item.seller_name || "seller"}
              </span>
              {reviews.rating.count > 0 && (
                <span className="flex items-center gap-1.5">
                  <Stars rating={reviews.rating.average} />
                  {reviews.rating.average.toFixed(1)} ({reviews.rating.count})
                </span>
              )}
            </div>
            <div className="mt-6 flex items-end justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Price</div>
                <div className="font-display text-5xl font-black tracking-tighter">
                  {formatPrice(item.listing_price)}
                </div>
              </div>
              <span data-testid="stock-badge" className={`nb-badge ${stock > 3 ? "bg-[#B4F0A7]" : stock > 0 ? "bg-[#FFDF85]" : "bg-[#FFA0A0]"}`}>
                {stock > 0 ? `${stock} in stock` : "Sold out"}
              </span>
            </div>
          </div>

          <div className="nb-card bg-[#FFDF85]">
            <span className="nb-label">Checkout</span>
            <div className="mt-4">
              <label className="nb-label">Quantity</label>
              <div className="mt-2 flex items-center gap-3">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="nb-btn nb-btn-ghost px-4 py-2">−</button>
                <input
                  data-testid="qty-input"
                  type="number"
                  min={1}
                  max={Math.max(1, stock)}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(stock || 1, Number(e.target.value) || 1)))}
                  className="nb-input w-24 text-center"
                />
                <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(stock || 1, q + 1))} className="nb-btn nb-btn-ghost px-4 py-2">+</button>
              </div>
            </div>

            <div className="mt-5">
              <label className="nb-label">Shipping</label>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {couriers.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`courier-${c.id}`}
                    onClick={() => setCourier(c.id)}
                    className={`flex items-center justify-between rounded-xl border-[3px] border-[#0A0A0A] p-3 text-left ${
                      courier === c.id ? "bg-[#D4C4FB] shadow-[3px_3px_0_0_#0A0A0A]" : "bg-white"
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 font-display text-base font-black">
                        <Truck weight="duotone" size={18} /> {c.name}
                      </div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-[#4A4A4A]">{c.eta}</div>
                    </div>
                    <div className="font-display text-lg font-black">{formatPrice(c.price)}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label className="nb-label">Shipping address (optional)</label>
              <textarea
                data-testid="address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                className="nb-input mt-2 resize-y"
                placeholder="Name, street, city, postcode"
              />
            </div>

            <div className="mt-6 space-y-1.5 rounded-xl border-[3px] border-[#0A0A0A] bg-white p-4">
              <div className="flex justify-between text-sm font-bold">
                <span>Subtotal ({qty} × {formatPrice(item.listing_price)})</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span>Shipping ({courierObj?.name})</span>
                <span>{formatPrice(shippingPrice)}</span>
              </div>
              <div className="mt-2 flex items-end justify-between border-t-[3px] border-[#0A0A0A] pt-2">
                <span className="font-display text-lg font-black">Total</span>
                <span data-testid="checkout-total" className="font-display text-3xl font-black tracking-tighter">{formatPrice(total)}</span>
              </div>
            </div>

            <button
              data-testid="buy-btn"
              onClick={buy}
              disabled={buying || stock <= 0 || isOwn}
              className="nb-btn mt-5 flex w-full items-center justify-center gap-2 disabled:opacity-60"
            >
              {buying ? (
                <><Sparkle weight="duotone" className="animate-spin" /> Redirecting to Stripe…</>
              ) : isOwn ? (
                <><Package weight="bold" size={18} /> Your own listing</>
              ) : stock <= 0 ? (
                <><Package weight="bold" size={18} /> Sold out</>
              ) : (
                <><ShoppingBagOpen weight="bold" size={18} /> Buy with Stripe</>
              )}
            </button>
            <p className="mt-2 text-center text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">
              Secure payment · Test mode
            </p>
            {isOwn && (
              <div className="mt-3 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider">
                <CheckCircle weight="bold" size={14} /> Manage this listing in your dashboard
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
